/**
 * @file productGroups.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-29
 * @description Product groups on the screens (Cam, 28 Oct 2026 — O1): the
 *              words and small rules Manage Inventory's Product Groups tab,
 *              the group window and the item window share. The server's own
 *              rules are utils/productGroups.js (names, the review plan).
 *
 *              1.1.0 (2026-10-29, Cam — N8): Search Inventory's Product group
 *              filter and column — the filter's options and what it says when
 *              closed, the column filter's "No group", a row's group as text
 *              (utils/inventoryGroupFilter.js is the server's SQL).
 */

export const NOT_READY_REASON = 'Not ready — migration product_groups.sql has not run';

/** Where the group to open is handed over when another screen links to it. */
export const OPEN_GROUP_KEY = 'productGroups.openGroupId';

const NAME_MAX = 120;

/** Why a group name cannot be saved, or null (the server says the same). */
export function groupNameProblem(value) {
  const name = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!name) return 'Give the group a name';
  if (name.length > NAME_MAX) return `A group name is at most ${NAME_MAX} characters`;
  return null;
}

/** "Bulk", "Totes", "25kg bags", "Totes, delivered", "Sold by the unit" — a pack key as a person reads it. */
export function packLabel(key) {
  const k = String(key || '');
  if (!k || k === 'unit') return 'Sold by the unit';
  const m = /^(.*?)(?: \((delivered|collected)\))?$/.exec(k);
  const base = m[1];
  let w;
  if (base === 'bulk') w = 'Bulk';
  else if (base === 'tote') w = 'Totes';
  else if (/bag$/.test(base)) w = `${base}s`;
  else w = base;
  w = w.charAt(0).toUpperCase() + w.slice(1);
  return m[2] ? `${w}, ${m[2]}` : w;
}

/** "NWF Agriculture" — the supplier an item is bought from (a depot under its group's name beside it). */
export function supplierLabel(supplier) {
  if (!supplier) return 'Not bought in 2 years';
  if (supplier.parent_name && supplier.parent_name !== supplier.name) return `${supplier.name} (${supplier.parent_name})`;
  return supplier.name;
}

/** The variant / additive label as shown: "Plain" for none. */
export const variantLabel = (additive) => (additive ? additive : 'Plain');

/** An answer's error words, else a fallback. */
export async function errorOf(res, fallback) {
  try {
    const data = await res.json();
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

/* ── Search Inventory (N8) ─────────────────────────────────────────────── */

/** The filter row's value for "an item in no group" (the server reads 'none'). */
export const NO_GROUP_VALUE = 'none';
export const NO_GROUP_LABEL = 'No group';
export const NO_GROUPS_YET = 'No product groups yet';

/** The column filter's blank, as the server sends it. */
const BLANK = '(blank)';

/**
 * Why the Product group filter cannot be used now, or null. `options` is the
 * answer of GET /api/inventory/product-group-options (null while it is read).
 */
export function groupFilterBlock(options) {
  if (!options) return 'Reading the product groups…';
  if (!options.ready) return options.reason || NOT_READY_REASON;
  if (!(options.groups || []).length) {
    return `${NO_GROUPS_YET} — make them on the Product Groups tab`;
  }
  return null;
}

/** The filter's options: "No group" first, then every live group by name. */
export function groupFilterOptions(options) {
  if (!options || !options.ready) return [];
  return [
    { value: NO_GROUP_VALUE, label: NO_GROUP_LABEL, searchText: `${NO_GROUP_LABEL} none ungrouped` },
    ...(options.groups || []).map(g => ({
      value: String(g.id),
      label: g.name,
      searchText: [g.name, g.category].filter(Boolean).join(' ')
    }))
  ];
}

/**
 * What the closed filter says: every group, the one chosen, "No group + 2
 * groups", or why it cannot be used.
 */
export function summarizeGroupFilter(options) {
  return (value) => {
    const block = groupFilterBlock(options);
    if (block) {
      if (!options) return 'Loading…';
      if (!options.ready) return 'Not ready';
      return NO_GROUPS_YET;
    }
    const sel = (value || []).map(String);
    if (!sel.length) return 'All Groups';
    const none = sel.includes(NO_GROUP_VALUE);
    const ids = sel.filter(v => v !== NO_GROUP_VALUE);
    const nameOf = (id) => (options.groups || []).find(g => String(g.id) === id)?.name;
    if (!ids.length) return NO_GROUP_LABEL;
    if (ids.length === 1 && !none) return nameOf(ids[0]) || '1 group';
    const groups = `${ids.length} group${ids.length === 1 ? '' : 's'}`;
    return none ? `${NO_GROUP_LABEL} + ${groups}` : groups;
  };
}

/** The Product group column filter's words for a value: its blank is "No group". */
export function groupColumnLabel(value) {
  return value === BLANK ? NO_GROUP_LABEL : value;
}

/** A search row's group as one line of text: "Harvester 18 (archived) — + Rumitech", or "—". */
export function groupCellText(item) {
  if (!item || !item.product_group_name) return '—';
  const name = item.product_group_archived ? `${item.product_group_name} (archived)` : item.product_group_name;
  return `${name} — ${variantLabel(item.product_group_additive)}`;
}
